/**
 * User Settings Pinia Store
 * 
 * MIGRATION FROM VUEX:
 * This store replaces the Vuex usersettings module (resources/assets/js/vuex/stores/usersettings.js)
 * 
 * Key differences from Vuex:
 * - No mutations: actions directly modify state using ref()
 * - No getters pattern: state refs are directly reactive
 * - Composition API style using defineStore()
 * - Manages its own localStorage persistence (separate from Vuex)
 * 
 * STORAGE:
 * - Persisted to localStorage under key: "${userEmail}@pinia"
 * - Structure: { userSettingsStore: { testDuration, apiAccount, ... } }
 * - Shares the same localStorage key as other Pinia stores for organizational purposes
 * 
 * USAGE IN MODELS:
 * The UserSettings model (extending AbstractPiniaModel) accesses this store:
 * - static piniaStore() { return useUserSettingsStore() }
 * - UserSettings.getTestDuration() → store.testDuration
 * - UserSettings.setTestDuration(value) → store.setTestDuration(value)
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getCurrentStorageUser } from '../../common/localStorage/currentStorageUser'

export const useUserSettingsStore = defineStore('userSettings', () => {
    // ========== STATE ==========
    // Using ref() for reactive state (Composition API pattern)
    
    /**
     * Complete settings object
     * Contains all user preferences and flags
     * Structure: { test_duration, api_account, welcome_random, welcome_quote, ... }
     */
    const settings = ref({})

    // ========== GETTERS (Computed) ==========
    // In Pinia, getters are simply computed properties
    
    /**
     * Get all settings
     */
    const getAllSettings = computed(() => settings.value)
    
    /**
     * Get test duration
     */
    const getTestDuration = computed(() => settings.value.test_duration)
    
    /**
     * Get API account info
     */
    const getApiAccount = computed(() => settings.value.api_account)
    
    /**
     * Get last selected profession
     */
    const getLastProfession = computed(() => settings.value.last_profession)
    
    /**
     * Get dashboard profession
     */
    const getDashboardProfession = computed(() => settings.value.dashboard_profession)
    
    /**
     * Get welcome random value
     */
    const getWelcomeRandom = computed(() => settings.value.welcome_random)
    
    /**
     * Get welcome quote value
     */
    const getWelcomeQuote = computed(() => settings.value.welcome_quote)
    
    /**
     * Check if marketing text is hidden for a slot
     * @param {number} slot - The slot number
     * @returns {boolean} Whether the marketing text is hidden
     */
    const isMarketingTextHidden = computed(() => (slot) => {
        return `hide_marketing_text:${slot}` in settings.value
    })
    
    /**
     * Check if renewal offer is hidden for a profession
     * @param {number} professionId - The profession ID
     * @returns {boolean} Whether the renewal offer is hidden
     */
    const isRenewalHidden = computed(() => (professionId) => {
        return `hide_renewal:${professionId}` in settings.value
    })

    // ========== ACTIONS ==========
    // In Pinia, actions are just functions that modify state
    
    /**
     * Set all user settings
     * Replaces the entire settings object
     * Preserves certain flags like welcome_random and welcome_quote
     * Syncs with localStorage after update
     * @param {Object} newSettings - New settings object
     */
    const setAllSettings = (newSettings) => {
        if (newSettings && settings.value && 'welcome_random' in settings.value)
            newSettings.welcome_random = settings.value.welcome_random
        if (newSettings && settings.value && 'welcome_quote' in settings.value)
            newSettings.welcome_quote = settings.value.welcome_quote
        settings.value = newSettings
        persistToLocalStorage()
    }

    /**
     * Set test duration
     * @param {*} duration - The test duration value
     */
    const setTestDuration = (duration) => {
        settings.value.test_duration = duration
        persistToLocalStorage()
    }

    /**
     * Set API account info
     * @param {*} account - The API account value
     */
    const setApiAccount = (account) => {
        settings.value.api_account = account
        persistToLocalStorage()
    }

    /**
     * Set the last used profession
     * @param {number} professionId - The profession ID
     */
    const setLastProfession = (professionId) => {
        settings.value.last_profession = professionId
        persistToLocalStorage()
    }

    /**
     * Set dashboard profession
     * @param {number} professionId - The profession ID
     */
    const setDashboardProfession = (professionId) => {
        settings.value.dashboard_profession = professionId
        persistToLocalStorage()
    }

    /**
     * Set welcome random value
     * @param {number} value - The random value
     */
    const setWelcomeRandom = (value) => {
        settings.value.welcome_random = value
        persistToLocalStorage()
    }

    /**
     * Set welcome quote value
     * @param {number} value - The quote value
     */
    const setWelcomeQuote = (value) => {
        settings.value.welcome_quote = value
        persistToLocalStorage()
    }

    /**
     * Hide marketing text for a specific slot
     * @param {number} slot - The slot number
     */
    const setHideMarketingText = (slot) => {
        settings.value[`hide_marketing_text:${slot}`] = true
        persistToLocalStorage()
    }

    /**
     * Hide renewal offer for a specific profession
     * @param {number} professionId - The profession ID
     */
    const setHideRenewal = (professionId) => {
        settings.value[`hide_renewal:${professionId}`] = true
        persistToLocalStorage()
    }

    // ========== LOCAL STORAGE PERSISTENCE ==========
    // Unlike Vuex (which uses VuexPersistence plugin), 
    // Pinia stores must manage their own persistence
    
    /**
     * Generate the localStorage key for this store
     * Format: "${userEmail}@pinia"
     * This keeps Pinia data separate from Vuex
     * @returns {string} Storage key
     */
    const getPiniaStorageKey = () => {
        return getCurrentStorageUser() + '@pinia'
    }

    /**
     * Save store state to localStorage
     * Called after every state mutation
     * Uses nested structure: { userSettingsStore: { testDuration, apiAccount, ... } }
     * @throws QuotaExceededError if localStorage is full
     */
    const persistToLocalStorage = () => {
        const key = getPiniaStorageKey()
        try {
            // Get existing Pinia localStorage data or create new
            let data = {}
            const stored = localStorage.getItem(key)
            if (stored) {
                try {
                    data = JSON.parse(stored)
                } catch (e) {
                    console.warn('Failed to parse existing Pinia localStorage, creating new:', e)
                    data = {}
                }
            }
            
            // Save entire settings object under userSettingsStore key
            // This preserves all fields including dynamic hide_* keys
            data.userSettingsStore = settings.value
            
            localStorage.setItem(key, JSON.stringify(data))
        } catch (e) {
            if (e.name === 'QuotaExceededError' || e.message.includes('quota')) {
                console.warn('🛒 QuotaExceededError detected in user settings store')
                throw e
            } else {
                console.error('❌ Error persisting user settings:', e)
            }
        }
    }

    /**
     * Load store state from localStorage on initialization
     * Called once when the store is created
     * Safely handles missing/corrupt data
     */
    const loadFromLocalStorage = () => {
        const key = getPiniaStorageKey()
        try {
            const stored = localStorage.getItem(key)
            if (stored) {
                const data = JSON.parse(stored)
                if (data.userSettingsStore && typeof data.userSettingsStore === 'object') {
                    // Restore entire settings object
                    settings.value = data.userSettingsStore
                }
            }
        } catch (e) {
            console.warn('Failed to load user settings from localStorage:', e)
        }
    }

    // ========== INITIALIZATION ==========
    loadFromLocalStorage()

    // ========== STORE INTERFACE ==========
    return {
        // State
        settings,
        // Getters (Computed)
        getAllSettings,
        getTestDuration,
        getApiAccount,
        getLastProfession,
        getDashboardProfession,
        getWelcomeRandom,
        getWelcomeQuote,
        isMarketingTextHidden,
        isRenewalHidden,
        // Actions
        setAllSettings,
        setTestDuration,
        setApiAccount,
        setLastProfession,
        setDashboardProfession,
        setWelcomeRandom,
        setWelcomeQuote,
        setHideMarketingText,
        setHideRenewal,
    }
})
